import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Briefcase } from "lucide-react";
import Header from "../components/Header";
import BeautyArsenalCard from "../components/BeautyArsenalCard";
import { track } from "../lib/analytics";

/**
 * Arsenal — dedicated page that hosts the Beauty Arsenal card so
 * returning users can jump straight from the /home dashboard into
 * their owned-products list without spelunking through Settings.
 *
 * Settings still hosts the same card (for the all-in-one preferences
 * surface) — this page is a focused entry point, not a duplicate
 * source of truth.
 */
const Arsenal = () => {
  const { t } = useTranslation();

  useEffect(() => {
    track("arsenal_opened", { surface: "arsenal_page" });
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="arsenal-page-root">
      <Header />
      <section className="pt-28 sm:pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link
              to="/home"
              className="inline-flex items-center gap-1.5 text-sm text-[#8A7D82] hover:text-[#9B6A6C] mb-6 transition-colors"
              data-testid="arsenal-back-to-home"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("arsenal.backToHome")}
            </Link>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#9B6A6C] mb-3">
              <Briefcase className="h-3.5 w-3.5" />
              {t("arsenal.kicker")}
            </span>
            <h1
              className="text-3xl sm:text-4xl font-light text-[#2C2123] leading-tight"
              data-testid="arsenal-page-heading"
            >
              {t("arsenal.heading")}{" "}
              <span className="italic font-serif text-[#9B6A6C]">
                {t("arsenal.headingItalic")}
              </span>
            </h1>
            <p className="text-[#5C4B51] mt-3 text-base leading-relaxed max-w-xl">
              {t("arsenal.subline")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.08 }}
          >
            <BeautyArsenalCard />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Arsenal;
